/**
 * Icon — the single glyph source for the app, backed by the GaussIQ Icon
 * System sprite (see icons/IconSprite.jsx, mounted once in main.jsx). Renders
 * a bare <svg> that inherits size/color from its parent via CSS, matching how
 * every inline SVG in this codebase already sized itself — so existing
 * wrapper styles (e.g. `.icon svg { width: 20px; height: 20px }`) keep working
 * unchanged.
 *
 * Accepts both the sprite's own kebab-case ids ('chevron-down', 'check-square')
 * and the legacy snake_case metric names the backend sends ('trending_up',
 * 'check_square') — ALIASES below bridges the two. An unresolved name falls
 * back to a neutral bar-chart glyph so a missing mapping never breaks the caller.
 *
 * Props:
 *   name      {string} — icon id, e.g. 'search', 'chevron-down', 'brain'
 *   className {string} — optional extra class(es) on the <svg>
 *   spin      {boolean} — optional; adds a continuous rotation (loading spinners)
 */

const ALIASES = {
  new_releases: 'insight',
  trending_up: 'trending-up',
  check_square: 'check-square',
  list: 'bars-stacked',
  alert: 'warning',
  arrow_right: 'arrow-right',
  arrow_up: 'arrow-up',
  arrow_down: 'arrow-down',
  hexagon_nodes: 'hexagon-nodes',
  bars_stacked: 'bars-stacked',
  // legacy local-registry names used across modals before the sprite migration
  members: 'people',
  lockOpen: 'lock-open',
  doc: 'document',
  pencil: 'edit',
  created: 'document',
  resolved: 'check-square',
  team: 'people',
  up: 'chevron-up',
  down: 'chevron-down',
}

// Every id defined in icons/IconSprite.jsx. Kept as a flat allowlist so an
// unrecognized `name` (e.g. a backend metric field with a typo) degrades to
// the neutral fallback glyph instead of rendering an empty <svg>.
const VALID_IDS = new Set([
  'home', 'skills', 'people', 'cases', 'knowledge', 'connect', 'settings',
  'sidebar-collapse', 'sidebar-expand',
  'search', 'close', 'check', 'chevron-up', 'chevron-down', 'chevron-left',
  'chevron-right', 'more', 'menu', 'plus', 'filter', 'edit', 'trash',
  'refresh', 'upload', 'download', 'link', 'lock', 'lock-open', 'logout', 'desktop',
  'arrow-left', 'arrow-right',
  'eye', 'eye-off', 'info', 'warning', 'bell', 'sun', 'moon', 'spinner', 'status-neutral',
  'user', 'pin',
  'document', 'folder', 'globe', 'layers', 'database',
  'brain', 'target', 'clock', 'shuffle', 'check-square', 'trending-up',
  'insight', 'snowflake', 'bars-stacked', 'analytics', 'arrow-up',
  'arrow-down', 'hexagon-nodes',
  'complexity', 'tag', 'thumb', 'friction',
  'mail', 'calendar',
  'skill-core', 'skill-technical', 'skill-general',
])

export default function Icon({ name, className = '', spin = false, ...rest }) {
  const resolved = ALIASES[name] ?? String(name || '').replace(/_/g, '-')
  const id = VALID_IDS.has(resolved) ? resolved : 'fallback'
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      style={spin ? { animation: 'gaussiq-icon-spin 1.1s linear infinite' } : undefined}
      aria-hidden="true"
      {...rest}
    >
      <use href={`#i-${id}`} />
    </svg>
  )
}
